import { getTrackReferenceId, isLocal } from '@livekit/components-core'
import { UseTracksOptions } from '@livekit/components-react'
import { Track } from 'livekit-client'

import { useTracks } from '@/src/hooks'
import { AudioTrack } from './participant/AudioTrack'

/** @public */
export interface RoomAudioRendererProps {
  /** Sets the volume for all audio tracks rendered by this component. By default, the range is between `0.0` and `1.0`. */
  volume?: number
  /**
   * If set to `true`, mutes all audio tracks rendered by the component.
   * @remarks
   * If set to `true`, the server will stop sending audio track data to the client.
   * @alpha
   */
  muted?: boolean
}

/**
 * The `RoomAudioRenderer` component is a drop-in solution for adding audio to your LiveKit app.
 * It takes care of handling remote participants’ audio tracks and makes sure that microphones and screen share are audible.
 *
 * @example
 * ```tsx
 * <LiveKitRoom>
 *   <RoomAudioRenderer />
 * </LiveKitRoom>
 * ```
 * @public
 */
const trackSources: Track.Source[] = [
  Track.Source.Microphone,
  Track.Source.ScreenShareAudio,
  Track.Source.Unknown,
]

const tracksOptions: UseTracksOptions = {
  onlySubscribed: false,
}

/**
 * Компонент для рендеринга аудио треков всех участников комнаты
 * 
 * ВАЖНО: Подписки на треки теперь управляются автоматически через хук useGroupTrackSubscriptions
 * в компоненте ActiveRoom. Этот компонент только рендерит уже подписанные треки.
 * 
 * Логика подписок:
 * - Подписки на треки участников управляются на основе их групповой принадлежности
 * - Подписываемся только на треки участников из той же группы, что и локальный участник
 * - При изменении группы подписки автоматически обновляются через события LiveKit
 */
export function RoomAudioRenderer({ volume, muted }: RoomAudioRendererProps) {
  // Получаем все аудио треки удаленных участников
  // onlySubscribed: false означает, что мы получаем все треки, но подписки управляются отдельно
  const tracks = useTracks(trackSources, tracksOptions).filter(
    (ref) => !isLocal(ref.participant) && ref.publication.kind === Track.Kind.Audio,
  )

  // Рендерим все аудио треки (компонент AudioTrack сам обрабатывает подписку через useMediaTrackBySourceOrName)
  return (
    <div style={{ display: 'none' }}>
      {tracks.map((trackRef) => (
        <AudioTrack
          key={getTrackReferenceId(trackRef)}
          trackRef={trackRef}
          volume={volume}
          muted={muted}
        />
      ))}
    </div>
  )
}
